// Servicios que el usuario en sesión PUEDE ABRIR (no solo ver) — misma lógica de gateo que
// la tarjeta de /servicios. Fuente única para: el menú lateral, los accesos rápidos del Home
// y el catálogo. Gateo por (audiencia × entitlement × telemetría).
//
// 🔴 NO concatenar la suite AMI de FluxAI (SCADA/red/reactiva/ISO) solo porque
// `puedePremium` es true (técnico/operador o grant de solar_study). Eso le pintaba
// Mitú/GEMSA y Balance al cliente de Solar Hub. SKU de FluxAI = contratado o nada.
import { SERVICE_CATALOG, esExclusivoFluxAI } from './catalog';
import { audience, isTecnico, puedePremium, tieneMonitoreoFluxAI, tieneServicio } from './auth';

/** Ventana inicial: todas las herramientas de campo para que cierre su primera cotización. */
export const ONBOARDING_TECNICO_DIAS = 60;
const ALTA_KEY = 'sh:tecnico-alta';

const CERRADO_VENTA = new Set(['ganada', 'cerrada']);
const CERRADO_OBRA = new Set(['completada', 'completado', 'instalado', 'instalada', 'finalizada']);

function delMes(iso, now) {
  if (!iso) return false;
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

function esDelTecnico(row, claims) {
  if (!row || !claims) return false;
  const id = claims.technician_id ?? claims.user_id ?? claims.id;
  const email = String(claims.email || '').toLowerCase();
  const tid = row.technician_id ?? row.tecnicoId ?? row.technicianId;
  const temail = String(row.technician_email || row.tecnicoEmail || row.tech_email || '').toLowerCase();
  if (id != null && tid != null && String(tid) === String(id)) return true;
  if (email && temail && temail === email) return true;
  return false;
}

function listasDeContexto(ctx = {}) {
  let quotes = Array.isArray(ctx.quotes) ? ctx.quotes : null;
  let ordenes = Array.isArray(ctx.ordenes) ? ctx.ordenes : null;
  if ((!quotes || !ordenes) && typeof localStorage !== 'undefined') {
    try {
      const q = JSON.parse(localStorage.getItem('al:quotes') || 'null');
      const o = JSON.parse(localStorage.getItem('al:ordenes') || 'null');
      if (!quotes) quotes = Array.isArray(q) ? q : [];
      if (!ordenes) ordenes = Array.isArray(o) ? o : [];
    } catch { /* modo privado */ }
  }
  return { quotes: quotes || [], ordenes: ordenes || [] };
}

/** Proyectos cerrados ESTE mes: venta ganada o obra instalada/completada del técnico. */
export function proyectosCerradosDelMes(claims, ctx = {}) {
  if (!isTecnico(claims)) return [];
  const now = ctx.now instanceof Date ? ctx.now : new Date();
  const { quotes, ordenes } = listasDeContexto(ctx);
  const out = [];
  for (const q of quotes) {
    if (!esDelTecnico(q, claims)) continue;
    if (!CERRADO_VENTA.has(String(q.status || '').toLowerCase())) continue;
    const when = q.cliente_acepto_en || q.closed_at || q.created_at || q.date;
    if (delMes(when, now)) out.push({ tipo: 'venta', id: q.id });
  }
  for (const o of ordenes) {
    if (!esDelTecnico(o, claims)) continue;
    if (o.tipo && o.tipo !== 'instalacion') continue;
    if (!CERRADO_OBRA.has(String(o.estado || o.status || '').toLowerCase())) continue;
    const when = o.cerradoEn || o.completed_at || o.installed_at || o.fecha || o.updated_at;
    if (delMes(when, now)) out.push({ tipo: 'obra', id: o.id });
  }
  return out;
}

function claveAlta(claims) {
  return String(claims?.email || claims?.technician_id || claims?.user_id || '').toLowerCase();
}

function leerAltas() {
  try { return JSON.parse(localStorage.getItem(ALTA_KEY) || '{}'); } catch { return {}; }
}

/** Primera vez que el técnico entra: se acuña el alta. Sirve para los 2 meses de onboarding. */
export function asegurarAltaTecnico(claims) {
  if (!isTecnico(claims) || typeof localStorage === 'undefined') return null;
  const k = claveAlta(claims);
  if (!k) return null;
  const all = leerAltas();
  if (all[k]) return new Date(all[k]);
  const fromClaims = claims.created_at || claims.tecnico_desde || (claims.iat ? claims.iat * 1000 : null);
  const iso = fromClaims ? new Date(fromClaims).toISOString() : new Date().toISOString();
  all[k] = iso;
  try { localStorage.setItem(ALTA_KEY, JSON.stringify(all)); } catch { /* modo privado */ }
  return new Date(iso);
}

export function fechaAltaTecnico(claims, ctx = {}) {
  if (!isTecnico(claims)) return null;
  if (ctx.alta) return ctx.alta instanceof Date ? ctx.alta : new Date(ctx.alta);
  const k = claveAlta(claims);
  const email = String(claims.email || '').toLowerCase();
  const inst = (ctx.installers || []).find((i) => (
    (i.email && email && String(i.email).toLowerCase() === email)
    || (claims.technician_id != null && String(i.id) === String(claims.technician_id))
  ));
  if (inst?.created_at) return new Date(inst.created_at);
  if (inst?.date) {
    const d = new Date(inst.date);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (typeof localStorage !== 'undefined' && k) {
    const guardada = leerAltas()[k];
    if (guardada) return new Date(guardada);
  }
  const fromClaims = claims.created_at || claims.tecnico_desde || (claims.iat ? claims.iat * 1000 : null);
  if (fromClaims) return new Date(fromClaims);
  return asegurarAltaTecnico(claims);
}

/** Primeros 60 días: todas las herramientas (estudio, seguimiento, órdenes, PQR). */
export function tecnicoEnOnboarding(claims, ctx = {}) {
  if (!isTecnico(claims)) return false;
  const alta = fechaAltaTecnico(claims, ctx);
  if (!alta || Number.isNaN(alta.getTime())) return true;
  const now = ctx.now instanceof Date ? ctx.now : new Date();
  return (now.getTime() - alta.getTime()) < ONBOARDING_TECNICO_DIAS * 24 * 60 * 60 * 1000;
}

/** Kit de último nivel para CERRAR: el técnico lo tiene siempre (estudio, 8760, unifilar, memoria). */
export function tecnicoTieneKitCierre(claims) {
  return isTecnico(claims);
}

/** Estudio gratis: kit de cierre, onboarding de 2 meses, o ≥ 1 proyecto cerrado este mes, o grant. */
export function tecnicoGanoEstudioGratis(claims, ctx = {}) {
  if (!isTecnico(claims)) return false;
  if (tecnicoTieneKitCierre(claims)) return true;
  if (tieneServicio('solar_study', claims)) return true;
  if (tecnicoEnOnboarding(claims, ctx)) return true;
  return proyectosCerradosDelMes(claims, ctx).length >= 1;
}

export function serviciosAccesibles(claims, ctx = {}) {
  const aud = audience(claims);
  const esAdmin = aud === 'admin';
  const premiumOk = puedePremium(claims);
  const conTele = tieneMonitoreoFluxAI(claims);
  return SERVICE_CATALOG.filter((s) => {
    if (esExclusivoFluxAI(s.sku)) return false;
    const visible = esAdmin || s.audiencia.includes(aud) || (aud == null && s.audiencia.includes(null));
    if (!visible) return false;
    if (esAdmin) return true;
    if (s.tier !== 'premium') return true;
    const requiereTele = s.requiere === 'monitoreo_premium';
    if (requiereTele && !conTele) return false;
    if (s.suite === 'fluxai') return tieneServicio(s.sku, claims);
    if (s.sku === 'solar_study') {
      // Producto PRINCIPAL de Solar Hub (junto al cotizador). Se ve y se abre
      // sin grant: el JWT de login casi nunca trae servicios[] y escondía el
      // estudio dejando solo la cotización gratuita. AMI de FluxAI sí exige contrato.
      return true;
    }
    if (s.requiereContrato) return tieneServicio(s.sku, claims);
    return premiumOk;
  });
}

/** Visible en menú/Home: todo el frente comercial. El proveedor no cotiza acá. */
export function veEstudioFotovoltaico(claims, ctx = {}) {
  if (audience(claims) === 'supplier') return false;
  return serviciosAccesibles(claims, ctx).some((s) => s.sku === 'solar_study');
}

/** Monitoreo nativo: equipos + ficha. No concatena SCADA. */
export function veMonitoreo(claims, ctx = {}) {
  return serviciosAccesibles(claims, ctx).some((s) => s.sku === 'monitoreo_premium');
}

/** El catálogo "Servicios" no es menú del usuario: es gestión (admin/operador). */
export function veCatalogoServicios(claims) {
  const a = audience(claims);
  return a === 'admin' || a === 'operator';
}
